import { LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetUserDetailQuery } from '@/modules/user/userApi';
import { useAppSelector } from '@/store/hooks';
import { getUserDisplayName, getUserRoleLabel } from '@/utils/user';
import { ProfileAvatarUpload } from './components/ProfileAvatarUpload';
import { PasswordTabContent } from './components/PasswordTabContent';
import { ProfileTabContent } from './components/ProfileTabContent';
import styles from './Account.module.scss';

type ProfileTab = 'profile' | 'password';

function resolveTab(value: string | null): ProfileTab {
  return value === 'password' ? 'password' : 'profile';
}

export default function Profile() {
  const [searchParams, setSearchParams] = useSearchParams();
  const authUser = useAppSelector((state) => state.auth.user);
  const { data: userDetail } = useGetUserDetailQuery();
  const user = userDetail ?? authUser;
  const activeTab = resolveTab(searchParams.get('tab'));
  const profileName = getUserDisplayName(user);
  const roleLabel = getUserRoleLabel(user?.role);

  const tabContent = useMemo(() => {
    if (activeTab === 'password') {
      return <PasswordTabContent />;
    }
    return <ProfileTabContent user={user} />;
  }, [activeTab, user]);

  const setTab = (tab: ProfileTab) => {
    if (tab === 'profile') {
      setSearchParams({});
      return;
    }
    setSearchParams({ tab });
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>My profile</h1>
        <p className={styles.pageSubtitle}>Manage your account details and security</p>
      </header>

      <div className={styles.profileLayout}>
        <nav className={styles.topTabs} role="tablist" aria-label="Profile settings">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'profile'}
            className={`${styles.topTab} ${activeTab === 'profile' ? styles.topTabActive : ''}`}
            onClick={() => setTab('profile')}
          >
            <UserOutlined />
            Profile details
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'password'}
            className={`${styles.topTab} ${activeTab === 'password' ? styles.topTabActive : ''}`}
            onClick={() => setTab('password')}
          >
            <LockOutlined />
            Password
          </button>
        </nav>

        <aside className={styles.profileSidebar}>
          <ProfileAvatarUpload user={user} />

          <h2 className={styles.userName}>{profileName}</h2>
          <span className={styles.roleBadge}>{roleLabel}</span>

          <p className={styles.emailMeta}>
            <MailOutlined />
            <span>{user?.email}</span>
          </p>
        </aside>

        <section className={styles.profileMain} role="tabpanel">
          {tabContent}
        </section>
      </div>
    </div>
  );
}
